import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styles from './CheckoutGuaranties.module.scss';
import messages from './messages';
import { CartInStore } from '../../../slices/cart/types';
import { getGuaranteeInfo } from '../../../utils/cart';
import { MATTRESS_PRODUCT_TYPE_KEY } from '../SteppedCheckout/constants';
import { GuaranteeInfo } from '../../../utils/cart/types';
import useGetGuaranteeItems from '../../../hooks/queries/useGetGuaranteeItems';

const CheckoutGuaranties = () => {
  const { formatMessage } = useIntl();

  const { cart } = useSelector((state: { cart: CartInStore }) => state);

  useGetGuaranteeItems({
    cartId: cart?.id,
    lineItemsIds:
      cart?.lineItems?.map((lineItem) => lineItem.id)?.join(',') ??
      'no-line-items',
  });

  const [guaranteeItemsToShow, setGuaranteeItemsToShow] = useState<
    GuaranteeInfo[]
  >([]);

  const cartWithMattress = !!cart?.lineItems?.find(
    (lineItem) => lineItem?.productType?.obj?.key === MATTRESS_PRODUCT_TYPE_KEY,
  );

  const { guaranteeItems } = cart;

  useEffect(() => {
    if (cartWithMattress) {
      const lineItemMattressIndexes = cart?.lineItems
        ?.map((lineItem, index) => {
          if (lineItem?.productType?.obj?.key === MATTRESS_PRODUCT_TYPE_KEY) {
            return index;
          }
          return undefined;
        })
        ?.filter((lineItemIndex) => typeof lineItemIndex === 'number');

      const mattressGuaranteesWithoutDuplicates = (
        guaranteeItems
          ?.filter((_, index) => lineItemMattressIndexes.includes(index))
          ?.flatMap((guarenteeItem) =>
            guarenteeItem
              ?.map((item) =>
                getGuaranteeInfo({ guarantee: item, formatMessage }),
              )
              .filter((guaranteeInfo) => guaranteeInfo !== null),
          ) as GuaranteeInfo[]
      )?.reduce((acc: GuaranteeInfo[], item: GuaranteeInfo) => {
        if (!acc.find((accItem) => accItem?.title === item?.title)) {
          return [...acc, item];
        }
        return acc;
      }, []);

      setGuaranteeItemsToShow(mattressGuaranteesWithoutDuplicates ?? []);
    } else {
      const guaranteesWithoutDuplicates = (
        guaranteeItems?.flatMap((guarenteeItem) =>
          guarenteeItem
            ?.map((item) =>
              getGuaranteeInfo({ guarantee: item, formatMessage }),
            )
            .filter((guaranteeInfo) => guaranteeInfo !== null),
        ) as GuaranteeInfo[]
      )?.reduce((acc: GuaranteeInfo[], item: GuaranteeInfo) => {
        if (!acc.find((accItem) => accItem?.title === item?.title)) {
          return [...acc, item];
        }
        return acc;
      }, []);

      setGuaranteeItemsToShow(guaranteesWithoutDuplicates ?? []);
    }
  }, [cart?.lineItems, cartWithMattress, formatMessage, guaranteeItems]);

  const guaranteeBannerFlyoutModalId =
    window.drupalSettings?.guarantee_banners?.[0]?.modal_id ?? '';
  const hasGuaranteeItems = guaranteeItemsToShow?.length > 0;
  return (
    <div className={styles['guaranties-checkout-container']}>
      {hasGuaranteeItems && (
        <div className={styles['guaranties-checkout-title']}>
          <div className={styles['guaranties-checkout-title-label']}>
            {cartWithMattress
              ? formatMessage(messages.worryFreeMattressGuarantee)
              : formatMessage(messages.worryFreeGuarantee)}
          </div>

          <div className={styles['guaranties-checkout-title-icon']}>
            <div
              className={styles['info-icon']}
              onClick={(e) => {
                e.preventDefault();
                window.MicroModal?.show(guaranteeBannerFlyoutModalId);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  window.MicroModal?.show(guaranteeBannerFlyoutModalId);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Guarantee information (opens in modal)"
            />
          </div>
        </div>
      )}
      <div className={styles['guaranties-checkout-content']}>
        {guaranteeItemsToShow.map((item) => (
          <div
            key={item.title}
            className={styles['guaranties-checkout-content-item']}
            onClick={(e) => {
              e.preventDefault();
              window.MicroModal?.show(guaranteeBannerFlyoutModalId);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                window.MicroModal?.show(guaranteeBannerFlyoutModalId);
              }
            }}
            role="button"
            // Ensure this div can be focused and activated with the keyboard.
            tabIndex={0}
            aria-label={`${item.title} information (opens in modal)`}
          >
            <div className={styles['guaranties-checkout-content-item-icon']}>
              <img
                src={item.icon as unknown as string}
                alt={`icon for ${item.title}`}
              />
            </div>
            <div className={styles['guaranties-checkout-content-item-label']}>
              {item.title}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CheckoutGuaranties;
